import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, getImageSrc } from "@/lib/api";
import { Filter, MapPin, Search, ShieldCheck } from "lucide-react";

const REGIONS = ["Greater Accra", "Ashanti", "Eastern", "Western", "Central", "Volta", "Northern", "Upper East", "Upper West", "Bono", "Bono East", "Ahafo", "Oti", "Savannah", "Western North", "North East"];
const CATEGORIES = [
  { key: "", label: "All" },
  { key: "livestock", label: "Livestock" },
  { key: "poultry", label: "Poultry" },
  { key: "agriculture", label: "Agriculture" },
  { key: "fishing", label: "Fishing" },
];

export default function Marketplace() {
  const [sp, setSp] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(sp.get("q") || "");

  const category = sp.get("category") || "";
  const region = sp.get("region") || "";

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category) params.category = category;
    if (region) params.region = region;
    if (sp.get("q")) params.q = sp.get("q");
    api.get("/products", { params }).then((r) => setProducts(r.data || [])).finally(() => setLoading(false));
  }, [category, region, sp]);

  const setParam = (key, val) => {
    const next = new URLSearchParams(sp);
    if (val) next.set(key, val); else next.delete(key);
    setSp(next);
  };

  const doSearch = (e) => {
    e.preventDefault();
    setParam("q", q);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-[#8C6D53]">Marketplace</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-[#1E4D3B] mt-1">
            {category ? category[0].toUpperCase() + category.slice(1) : "All produce, livestock & catch"}
          </h1>
        </div>
        <form onSubmit={doSearch} className="flex items-center bg-white border border-[#E5D9C8] rounded-xl overflow-hidden">
          <Search className="w-4 h-4 ml-3 text-[#5C6660]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search maize, tilapia, goats..."
            className="px-3 py-2 outline-none text-sm w-64"
            data-testid="marketplace-search-input"
          />
          <button className="btn-primary px-4 py-2 text-sm" data-testid="marketplace-search-submit">Search</button>
        </form>
      </div>

      <div className="grid md:grid-cols-[240px_1fr] gap-6">
        {/* Filters */}
        <aside className="card-farm p-5 h-fit">
          <div className="flex items-center gap-2 text-[#1E4D3B] font-bold mb-4"><Filter className="w-4 h-4" /> Filters</div>
          <div className="text-xs uppercase tracking-widest font-bold text-[#8C6D53] mb-2">Category</div>
          <div className="flex flex-col gap-1 mb-5">
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                onClick={() => setParam("category", c.key)}
                className={`text-left px-3 py-1.5 rounded-lg text-sm ${category === c.key ? "bg-[#1E4D3B] text-white font-semibold" : "hover:bg-[#F3ECE2]"}`}
                data-testid={`filter-cat-${c.key || "all"}`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="text-xs uppercase tracking-widest font-bold text-[#8C6D53] mb-2">Region</div>
          <select
            value={region}
            onChange={(e) => setParam("region", e.target.value)}
            className="w-full px-3 py-2 border border-[#E5D9C8] rounded-lg text-sm bg-white"
            data-testid="filter-region-select"
          >
            <option value="">All regions</option>
            {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </aside>

        {/* Grid */}
        <div>
          {loading ? (
            <div className="text-[#5C6660]">Loading fresh listings...</div>
          ) : products.length === 0 ? (
            <div className="card-farm p-10 text-center">
              <div className="font-bold text-lg">No listings found</div>
              <div className="text-sm text-[#5C6660] mt-1">Try clearing filters or search elsewhere.</div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="products-grid">
              {products.map((p) => (
                <Link key={p.id} to={`/product/${p.id}`} className="card-farm overflow-hidden flex flex-col" data-testid={`product-card-${p.id}`}>
                  <div className="h-52 overflow-hidden bg-[#F3ECE2]">
                    <img src={getImageSrc(p)} alt={p.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-4 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="badge-cat">{p.category}</span>
                      <span className="inline-flex items-center gap-1 text-xs text-[#5C6660]"><MapPin className="w-3 h-3" /> {p.region}</span>
                    </div>
                    <div className="font-bold text-[#1F2421]">{p.name}</div>
                    <div className="text-xs text-[#5C6660] mt-1 inline-flex items-center gap-1">by {p.seller_name} {p.seller_verified && <ShieldCheck className="w-3 h-3 text-[#1B804E]" />}</div>
                    <div className="mt-auto pt-3 flex items-baseline justify-between">
                      <div className="text-[#C85A32] font-black text-xl">GH₵ {p.price.toFixed(2)}</div>
                      <div className="text-xs text-[#5C6660]">per {p.unit}</div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
